import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

const ThinkingOverlay = ({ show, messages, duration = 3000, onComplete }) => {
  const [currentMsg, setCurrentMsg] = useState(0);
  const [visible, setVisible] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!show) { setVisible(false); setCurrentMsg(0); return; }
    setVisible(true);
    setCurrentMsg(0);

    const msgInterval = duration / Math.max(messages.length, 1);
    const timer = setInterval(() => {
      setCurrentMsg((prev) => {
        if (prev < messages.length - 1) return prev + 1;
        return prev;
      });
    }, msgInterval);

    const done = setTimeout(() => {
      clearInterval(timer);
      setVisible(false);
      if (onCompleteRef.current) onCompleteRef.current();
    }, duration);

    return () => { clearInterval(timer); clearTimeout(done); };
  }, [show, messages.length, duration]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="thinking-overlay">
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center animate-in fade-in zoom-in-95 duration-300">
        <div className="flex justify-center mb-5">
          <div className="relative">
            <div className="h-14 w-14 rounded-2xl bg-[var(--brand)]/15 flex items-center justify-center">
              <Loader2 className="h-7 w-7 text-[var(--brand)] animate-spin" />
            </div>
            <div className="absolute -inset-2 rounded-3xl bg-[var(--brand)]/5 animate-pulse" />
          </div>
        </div>
        <p className="text-sm font-semibold text-foreground mb-2 transition-all duration-500" key={currentMsg}>
          {messages[currentMsg]}
        </p>
        <div className="flex justify-center gap-1.5 mt-4">
          {messages.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i <= currentMsg ? "w-6 bg-[var(--brand)]" : "w-1.5 bg-foreground/15"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThinkingOverlay;
